import { useLingui } from '@lingui/react/macro';
import React, { useMemo } from 'react';
import { UDSelect } from 'ud-form/components/select/component';
import overriddenSelectComponents from 'ud-form/components/select/component/override';
import UDText from 'ud-ui/components/text';
import { ChampionshipFilterItem, ChampionshipFilterOption } from 'modules/schedule/domain/interfaces/championship';
import { splitChampionshipName } from 'modules/schedule/domain/utils/championship-filter';

interface ChampionshipFilterProps {
  options: ChampionshipFilterOption[];
  value: ChampionshipFilterItem[];
  onChange: (items: ChampionshipFilterItem[]) => void;
}

interface FilterSelectOption {
  value: number;
  label: string;
  description?: string;
  count: number;
  /** Полное название — по нему идёт поиск: площадка в списке вынесена во вторую строку, но искать по ней тоже надо. */
  data: { name: string };
}

/** Закрытое поле — не чипы, а итог выбора («Турниры: 2»): по длинным названиям чипы заняли бы несколько строк. */
const SummaryPlaceholder = (props: { children: React.ReactNode; innerProps: React.HTMLAttributes<HTMLElement> }) => (
  <UDText
    type="subhead"
    className="text-inkPrimary pointer-events-none absolute top-1/2 left-4 -translate-y-1/2"
    {...props.innerProps}
  >
    {props.children}
  </UDText>
);

const components = { ...overriddenSelectComponents, Placeholder: SummaryPlaceholder };

const toSelectOption = ({ id, name, count }: ChampionshipFilterOption): FilterSelectOption => {
  const { title, venue } = splitChampionshipName(name);
  return { value: id, label: title, description: venue, count, data: { name } };
};

/**
 * Фильтр расписания по турнирам: выпадающий список с поиском, применяется сразу, без «Применить». У пункта — галочка,
 * число матчей в показанных месяцах и площадка второй строкой. Снять выбор — крестик в поле или «снять все» в меню.
 */
const ChampionshipFilter = ({ options, value, onChange }: ChampionshipFilterProps) => {
  const { t } = useLingui();

  const selectOptions = useMemo(() => options.map(toSelectOption), [options]);
  const selected = useMemo(
    () => selectOptions.filter((option) => value.some(({ id }) => id === option.value)),
    [selectOptions, value],
  );
  const selectedCount = value.length;
  const summary = selectedCount === 0 ? t`Турниры: все` : t`Турниры: ${selectedCount}`;

  return (
    <div className="w-full sm:w-72" data-testid="championship-filter">
      <UDSelect
        isMulti
        isSearchable
        isClearable
        controlShouldRenderValue={false}
        aria-label={t`Турниры`}
        placeholder={summary}
        options={selectOptions}
        value={selected}
        components={components}
        filterOption={(option, input) => option.data.data.name.toLowerCase().includes(input.toLowerCase())}
        onChange={(items) => onChange(items.map((item) => ({ id: Number(item.value), name: item.data.name })))}
      />
    </div>
  );
};

export default React.memo(ChampionshipFilter);
